import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, StatusPill, Segmented, Spinner, Button } from "../components/ui";
import HealthStrip from "../components/HealthStrip";
import AttentionBanner from "../components/AttentionBanner";
import MyMentions from "../components/MyMentions";
import NetWorthWidget from "../components/NetWorthWidget";
import ExpiringContractsWidget from "../components/ExpiringContractsWidget";
import AdvisoryFollowupsWidget from "../components/AdvisoryFollowupsWidget";
import ProjectDeliveryWidget from "../components/ProjectDeliveryWidget";
import SalesPipelineWidget from "../components/SalesPipelineWidget";
import { formatEUR, formatEURSigned, cn } from "../lib/utils";
import {
  ArrowUpRight, TrendingUp, TrendingDown, Bell, LineChart,
  Home, FileText, ShoppingCart, Clock, FileWarning, Sparkles, Check,
} from "lucide-react";

const ALERT_ICONS = {
  invoice: Clock,
  rent: Home,
  document: FileText,
  portfolio: LineChart,
};
const ALERT_COLORS = {
  high: { bg: "rgba(239,68,68,0.15)", color: "#F87171" },
  medium: { bg: "rgba(245,158,11,0.15)", color: "#FBBF24" },
  info: { bg: "rgba(37,99,235,0.15)", color: "#60A5FA" },
};

function KpiTile({ label, value, sub, subColor, onClick, testid }) {
  return (
    <Card hover onClick={onClick} data-testid={testid} className="!p-5">
      <SectionLabel>{label}</SectionLabel>
      <div className="text-3xl font-heading font-medium tabular-nums mt-2 tracking-tight">{value}</div>
      {sub && <div className="text-xs mt-1.5 font-medium" style={{ color: subColor }}>{sub}</div>}
    </Card>
  );
}

// Renders **bold** as real <strong> nodes; everything else is plain text that React
// auto-escapes. The briefing text is LLM-generated, so we never use
// dangerouslySetInnerHTML — markup in the source can't execute (no XSS vector).
function renderInline(s, keyPrefix) {
  return s.split(/(\*\*.+?\*\*)/g).filter(Boolean).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>
  );
}

function MarkdownLite({ text }) {
  // minimal markdown: # heading, **bold**, - / * bullets
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      out.push(<ul key={`ul-${key}`}>{list.map((li, i) => <li key={`li-${key}-${i}`}>{renderInline(li, `li-${key}-${i}`)}</li>)}</ul>);
      list = [];
    }
  };
  lines.forEach((l, i) => {
    const line = l.trim();
    if (line.startsWith("# ")) { flush(i); out.push(<h1 key={`h-${i}`}>{line.slice(2)}</h1>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { list.push(line.slice(2)); }
    else if (line) { flush(i); out.push(<p key={`p-${i}`}>{renderInline(line, `p-${i}`)}</p>); }
  });
  flush("end");
  return <div className="md-content">{out}</div>;
}

export default function Dashboard() {
  const { t, period, setPeriod, lang, entities, entityHealth } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const gotoEntity = (entityId) => {
    const e = entities.find((x) => x.id === entityId);
    if (e) navigate(`/entity/${e.key}`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/dashboard?period=${period}`);
      setData(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Show today's briefing automatically; if none exists yet, auto-generate ONCE per day
  // (credit guard: a per-day/lang localStorage flag + the server only keeps one/day).
  useEffect(() => {
    let active = true;
    setAi(null);
    (async () => {
      try {
        const { data } = await api.get(`/ai/daily-summary/latest?lang=${lang}`);
        if (!active) return;
        if (data.text) { setAi(data.text); return; }
        const today = new Date().toISOString().slice(0, 10);
        const flag = `ai_autogen_${today}_${lang}`;
        if (localStorage.getItem(flag)) return;
        localStorage.setItem(flag, "1");
        setAiLoading(true);
        const res = await api.post(`/ai/daily-summary?lang=${lang}`, {});
        if (active && res.data?.text) setAi(res.data.text);
      } catch (err) {
        // best-effort auto-briefing; a failure here (e.g. LLM budget) must not break the dashboard
        console.debug("AI daily-summary auto-generate skipped:", err?.message || err);
      } finally {
        if (active) setAiLoading(false);
      }
    })();
    return () => { active = false; };
  }, [lang]);

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/daily-summary?lang=${lang}`, {});
      setAi(data.text);
    } catch {
      setAi(lang === "fr" ? "Impossible de générer le briefing pour le moment." : "Could not generate the briefing right now.");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;
  }
  if (error || !data) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4" data-testid="dashboard-error">
        <p className="text-text-secondary text-sm">{lang === "fr" ? "Erreur de chargement du tableau de bord." : "Failed to load the dashboard."}</p>
        <Button onClick={load} data-testid="dashboard-retry">{lang === "fr" ? "Réessayer" : "Retry"}</Button>
      </div>
    );
  }

  const h = data.headline;
  const maxCf = Math.max(...data.cashflow_by_entity.map((c) => Math.abs(c.amount)), 1);

  const periodOptions = [
    { value: "month", label: t("period_month") },
    { value: "ytd", label: t("period_ytd") },
    { value: "all", label: t("period_all") },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <h1 className="text-3xl lg:text-4xl font-heading font-medium tracking-tight">{t("group_hq")}</h1>
          <p className="text-text-secondary text-sm mt-1 capitalize">
            {data.month_label} · {data.entities_count} {t("entities")}
          </p>
        </div>
        <Segmented options={periodOptions} value={period} onChange={setPeriod} testid="period-toggle" />
      </div>

      {/* Needs-your-attention banner — riskiest entity + top reason */}
      <AttentionBanner entities={entities} entityHealth={entityHealth} />

      {/* My @mentions inbox (renders only when there are mentions) */}
      <MyMentions />

      {/* Contracts expiring soon — cross-entity (renders only when there are any) */}
      <ExpiringContractsWidget />

      {/* Advisory decision follow-ups due — owner-only (renders only when there are any) */}
      <AdvisoryFollowupsWidget />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <KpiTile
          testid="kpi-cashflow"
          label={t("kpi_net_cashflow")}
          value={formatEURSigned(h.net_cashflow)}
          sub={`${h.net_cashflow_trend >= 0 ? "↑" : "↓"} ${t("vs_last_month")}`}
          subColor={h.net_cashflow_trend >= 0 ? "#34D399" : "#F87171"}
          onClick={() => {}}
        />
        <KpiTile
          testid="kpi-tasks"
          label={t("kpi_open_tasks")}
          value={h.open_tasks}
          sub={`${h.overdue_tasks} ${t("overdue")}`}
          subColor={h.overdue_tasks > 0 ? "#F87171" : "#A1A1AA"}
          onClick={() => navigate("/okr")}
        />
        <KpiTile
          testid="kpi-portfolio"
          label={t("kpi_portfolio")}
          value={formatEUR(h.portfolio_value)}
          sub={`${h.portfolio_change >= 0 ? "+" : ""}${h.portfolio_change}% ${t("mtd")}`}
          subColor={h.portfolio_change >= 0 ? "#34D399" : "#F87171"}
          onClick={() => navigate("/entity/holding")}
        />
        <KpiTile
          testid="kpi-rent"
          label={t("kpi_rent")}
          value={`${h.rent_collected} / ${h.rent_total}`}
          sub={`${h.rent_pending} ${t("pending")}`}
          subColor={h.rent_pending > 0 ? "#FBBF24" : "#34D399"}
          onClick={() => navigate("/entity/sci")}
        />
      </div>

      {/* Entity health strip */}
      <HealthStrip entities={entities} entityHealth={entityHealth} t={t} navigate={navigate} />

      {/* AI briefing */}
      <Card className="mb-6 !p-5" data-testid="ai-briefing-card">
        <div className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "#60A5FA" }} />
            <h3 className="font-heading font-medium">{t("ai_briefing")}</h3>
          </div>
          <Button size="sm" variant={ai ? "outline" : "primary"} onClick={generateAi} disabled={aiLoading} data-testid="ai-generate-button">
            {aiLoading ? <Spinner className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
            {aiLoading ? t("ai_generating") : ai ? t("ai_regenerate") : t("ai_generate")}
          </Button>
        </div>
        {ai && <div className="mt-3 pt-3 border-t" data-testid="ai-briefing-content"><MarkdownLite text={ai} /></div>}
      </Card>

      {/* Row: cashflow + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <Card className="lg:col-span-7">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-medium">{t("cashflow_by_entity")}</h3>
            </div>
            <span className="text-xs text-text-secondary capitalize">{data.month_label}</span>
          </div>
          <div className="space-y-5">
            {data.cashflow_by_entity.map((c) => {
              const pct = (Math.abs(c.amount) / maxCf) * 100;
              const neg = c.amount < 0;
              return (
                <button
                  key={c.entity_id}
                  onClick={() => gotoEntity(c.entity_id)}
                  data-testid={`cashflow-row-${c.key}`}
                  className="w-full text-left group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                      {c.name}
                    </span>
                    <span className="text-sm font-medium tabular-nums" style={{ color: neg ? "#F87171" : "var(--text-primary)" }}>
                      {formatEURSigned(c.amount)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
                    <div
                      className="h-full rounded-full transition-all duration-500 group-hover:opacity-80"
                      style={{ width: `${pct}%`, backgroundColor: neg ? "#EF4444" : c.color }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="lg:col-span-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-medium">{t("alerts")}</h3>
            </div>
            <span className="text-xs text-text-secondary">{data.alerts.length} {t("items")}</span>
          </div>
          <div className="space-y-1 max-h-[260px] overflow-y-auto -mr-2 pr-2">
            {data.alerts.length === 0 && (
              <p className="text-sm text-text-secondary py-6 text-center">{t("no_alerts")}</p>
            )}
            {data.alerts.map((a, i) => {
              const Icon = ALERT_ICONS[a.type] || FileWarning;
              const col = ALERT_COLORS[a.severity] || ALERT_COLORS.info;
              return (
                <button
                  key={i}
                  onClick={() => a.entity_id && gotoEntity(a.entity_id)}
                  data-testid={`alert-item-${i}`}
                  className="w-full flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface-hover transition-colors text-left"
                >
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: col.bg }}>
                    <Icon className="w-4 h-4" style={{ color: col.color }} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{a.title}</span>
                    <span className="block text-xs text-text-secondary mt-0.5">{a.detail}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Row: net worth */}
      <div className="mb-6">
        <NetWorthWidget className="flex flex-col" />
      </div>

      {/* Cross-entity project delivery rollup */}
      <ProjectDeliveryWidget />

      {/* Cross-entity sales pipeline rollup */}
      <SalesPipelineWidget />

      {/* Row: rents + invoices + ecommerce */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card data-testid="panel-rents">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Home className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-medium">{t("rents")}</h3>
            </div>
            <button onClick={() => navigate("/entity/sci")} className="text-xs text-sci hover:opacity-80 inline-flex items-center gap-1">
              SCI <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {data.rents.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">{r.label}</span>
                <span className="flex items-center gap-2.5 shrink-0">
                  <StatusPill status={r.status} label={t(r.status === "paid" ? "done" : "pending") && (r.status === "paid" ? (lang === "fr" ? "Payé" : "Paid") : (lang === "fr" ? "En attente" : "Pending"))} />
                  <span className="font-medium tabular-nums w-14 text-right">{formatEUR(r.amount)}</span>
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card data-testid="panel-invoices">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-medium">{t("invoices")}</h3>
            </div>
            <button onClick={() => navigate("/entity/consulting")} className="text-xs text-consulting hover:opacity-80 inline-flex items-center gap-1">
              Consulting <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {data.invoices.map((iv) => (
              <div key={iv.id} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">{iv.client_name}</span>
                <span className="flex items-center gap-2.5 shrink-0">
                  <StatusPill status={iv.status} label={iv.status === "late" ? (lang === "fr" ? "Retard" : "Late") : iv.status === "sent" ? (lang === "fr" ? "Envoyée" : "Sent") : iv.status === "paid" ? (lang === "fr" ? "Payée" : "Paid") : iv.status} />
                  <span className="font-medium tabular-nums w-16 text-right">{formatEUR(iv.amount)}</span>
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card data-testid="panel-ecommerce">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-medium">{t("ecommerce")}</h3>
            </div>
            <button onClick={() => navigate("/entity/ecommerce")} className="text-xs text-ecommerce hover:opacity-80 inline-flex items-center gap-1">
              {t("detail")} <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl p-3" style={{ background: "var(--muted)" }}>
              <SectionLabel>{t("revenue")}</SectionLabel>
              <div className="text-xl font-heading font-medium tabular-nums mt-1">{formatEUR(data.ecommerce.revenue)}</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: "var(--muted)" }}>
              <SectionLabel>{t("orders")}</SectionLabel>
              <div className="text-xl font-heading font-medium tabular-nums mt-1">{data.ecommerce.orders}</div>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-text-secondary">{t("avg_margin")}</span><span className="font-medium">{data.ecommerce.avg_margin}%</span></div>
            <div className="flex justify-between"><span className="text-text-secondary">{t("low_stock")}</span><span className="font-medium text-red-400">{data.ecommerce.low_stock} SKU</span></div>
            <div className="flex justify-between"><span className="text-text-secondary">{t("active_campaigns")}</span><span className="font-medium">{data.ecommerce.active_campaigns}</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
